import React, { memo, useState } from 'react';
import NextImage from 'next/image';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import { MdContentCopy } from '@react-icons/all-files/md/MdContentCopy';

import { SocialLink } from './style';
import { ISocialShare } from './model';

import { MdShare } from '@react-icons/all-files/md/MdShare';
import Button from '@core/design-system/button';
import { useSnackbar } from '@core/design-system/snackbar';

export const SocialSharePopover = memo(
  ({ description, tooltipText, url, style }: ISocialShare) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const { openSnackbar } = useSnackbar();

    const clickAwayHandler = () => {
      setOpen(false);
    };

    const handleMouseEnter = (event: React.MouseEvent<any>) => {
      setAnchorEl(event.currentTarget);
      setOpen(true);
    };

    const handlePopoverClose = () => {
      setOpen(false);
    };

    const copyLink = async (event: React.MouseEvent) => {
      await navigator.clipboard.writeText(url);

      openSnackbar({
        message: 'Link copiado para a área de transferência!',
        type: 'success',
        vertical: 'top',
      });

      handlePopoverClose();
    };

    return (
      <>
        <Button
          iconButton
          onMouseEnter={handleMouseEnter}
          aria-label="Compartilhar"
          style={style}
        >
          <MdShare size={19} />
        </Button>

        <Popper
          style={{ zIndex: 901 }}
          open={open}
          anchorEl={anchorEl}
          placement="bottom"
          transition
          onMouseLeave={handlePopoverClose}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={clickAwayHandler}>
              <Fade {...TransitionProps} timeout={350}>
                <Box
                  bgcolor="#fff"
                  padding="16px"
                  boxShadow={3}
                  display="flex"
                  justifyContent="space-between"
                  borderRadius="5px"
                >
                  <Button iconButton onClick={copyLink} sx={{ border: 'none' }}>
                    <MdContentCopy />
                  </Button>

                  <SocialLink
                    href={`//twitter.com/intent/tweet?text=${description}&url=${url}`}
                    title={`${tooltipText} no Twitter`}
                    rel="nofollow noopener"
                    target="_blank"
                  >
                    <NextImage
                      src="/img/share-twitter.png"
                      width={30}
                      height={30}
                      alt="Twitter"
                    />
                  </SocialLink>

                  <SocialLink
                    href={`//www.facebook.com/sharer.php?u=${url}`}
                    title={`${tooltipText} no Facebook`}
                    rel="nofollow noopener"
                    target="_blank"
                  >
                    <NextImage
                      src="/img/share-facebook.png"
                      width={30}
                      height={30}
                      alt="Facebook"
                    />
                  </SocialLink>

                  <SocialLink
                    href={`//www.linkedin.com/shareArticle?mini=true&url=${url}`}
                    title={`${tooltipText} no Linkedin`}
                    rel="nofollow noopener"
                    target="_blank"
                  >
                    <NextImage
                      src="/img/share-linkedin.png"
                      width={30}
                      height={30}
                      alt="Linkedin"
                    />
                  </SocialLink>

                  <SocialLink
                    href={`mailto:?Body=${description} ${url}`}
                    title={tooltipText}
                    rel="nofollow noopener"
                    target="_blank"
                  >
                    <NextImage
                      src="/img/share-email.png"
                      width={30}
                      height={30}
                      alt="Email"
                    />
                  </SocialLink>

                  <SocialLink
                    href={`//telegram.me/share/url?text=${description}&url=${url}`}
                    title={`${tooltipText} no Telegram`}
                    rel="nofollow noopener"
                    target="_blank"
                  >
                    <NextImage
                      src="/img/share-telegram.png"
                      width={30}
                      height={30}
                      alt="Telegram"
                    />
                  </SocialLink>
                  <SocialLink
                    href={`//api.whatsapp.com/send?text=${description} ${url}`}
                    title={`${tooltipText} no Whatsapp`}
                    rel="nofollow noopener"
                    target="_blank"
                  >
                    <NextImage
                      src="/img/share-whatsapp.png"
                      width={30}
                      height={30}
                      alt="Whatsapp"
                    />
                  </SocialLink>
                </Box>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </>
    );
  }
);
